/**
 * Der „Schmied“ spricht mit — eigene Stimme und Anrede. (15.10.2026, Dreier-Modus, shared/entwickler-im-chat.ts)
 *
 * Martin und Vespera nennen die Entwickler-Sitzung am Server „Schmied“. Martin, 21:19: er soll eine EIGENE hörbare Stimme
 * bekommen, damit er mitreden kann — und das Mikro muss dazu passen. Vesperas Vorschlag, von Martin als richtig gesehen:
 * „Das Mikro geht nur auf, wenn der letzte Satz an Martin gerichtet ist. Spricht Vespera den Schmied an, bleibt es zu, bis
 * seine Antwort da ist. Kommt nach etwa 20 Sekunden nichts, geht es trotzdem auf, damit Martin nicht festhängt.“
 *
 * EIN Besitzer für:
 *   - den Schalter „Entwickler vorlesen“ (nur Konto 1, Vorgabe AUS — nur ein ausdrückliches `true` schaltet ein);
 *   - die Stimme des Schmieds (SCHMIED_STIMMEN, `schmiedStimmeAus`) und seinen Namen in der Anzeige;
 *   - die Anrede: spricht Vespera am Ende ihrer Antwort den Schmied an (`sprichtSchmiedAn`)?
 *   - die Frist, nach der das Mikro auch ohne seine Antwort aufgeht (SCHMIED_WARTEN_MS).
 *
 * DIE STIMME (Martin, 21:24: „aus Soniox, ruhig/tief als Vorgabe“, selbst änderbar unter „Stimme des Entwicklers“):
 * Vorgabe ist „Jonas“ (soniox:Jonas), mit eigenem Tempo (SCHMIED_TEMPO). Wunsch: ruhig, eher tief,
 * handwerklich-bodenständig, deutlich anders als Konrad/Marlene und die Meditationsstimmen (Konrad, Marlene, Grace, Daniel,
 * Iris, Nathan — shared/meditations-stimme.ts; Nathan ist zugleich Martins Vorlese-Stimme). Belegt ist nur, was
 * GET /v1/tts-models (Modell tts-rt-v2, 24.09.) sagt: Jonas, Florian (männlich) und Annika (weiblich) sind weitere RUHIGE,
 * DEUTSCHE Muttersprach-Stimmen (shared/meditations-stimme.ts). Jonas ist männlich wie Vesperas Nathan, aber eine
 * deutsche Muttersprach-Stimme statt einer englischen; das trennt ihn hörbar von Vespera.
 * ⚠️ Ich kann nicht hören: ob Jonas oder Florian tiefer klingt, entscheidet Martin in der Auswahl.
 * Gesprochen wird über den vorhandenen Soniox-Weg von /api/voice-preview (Präfix „soniox:“) — kein neuer Schlüssel, kein
 * neuer Dienst. Das Tempo macht dort ffmpeg (mp3Tempo, atempo 0,5–2,0) oder — bei `ttsTempoNativ` — Soniox selbst (`speed`,
 * nur 0,7–1,3, server/soniox-schneller-weg.ts). 0,8 klang „wie ein Betrunkener“ (Ticket 2569); 0,95 war Martin (22:37) zu
 * langsam. 1,08 ist „etwas schneller“: liegt in BEIDEN Bereichen (keiner der Wege fällt still auf 1 zurück) und deutlich
 * unter Vesperas üblichem Tempo 1,2 (soniox-schneller-weg.ts), damit er ruhig bleibt.
 *
 * ⚠️ Vorlesen ändert nichts daran, was ein Beitrag IST: Fremdtext, nie Martins Ja, nie ein Auftrag (server/dreier-modus.ts).
 *    Gelesen wird nur, was als Beitrag über die angemeldete Connect-Tür kam (`istEntwicklerZeile`, Bus-Quelle „entwickler“).
 */
import { ZUSATZ_IDS, entwickler, type EntwicklerId, type ZusatzId } from "./entwickler.js";
import { entwicklerImChatFuerKonto } from "./entwickler-im-chat.js";
import { ABSCHIED, DANK, FUELLER, bereinigt } from "./abschied-worte.js";

/** Der Schmied in der Liste der Entwickler (shared/entwickler.ts, seit 16.10.2026 der eine Besitzer von Name, Stimme, Tempo, Rufname). */
const SCHMIED = entwickler("schmied");

/** Der Schlüssel in den Einstellungen des Kontos. */
export const ENTWICKLER_VORLESEN_FELD = "entwicklerVorlesen";

/** So heißt er in der Anzeige (Sprech-Pille) und in der Anrede. */
export const SCHMIED_NAME = SCHMIED.name;

/** Was in „Stimme des Entwicklers“ zur Wahl steht — Soniox-Stimmen, die auf tts-rt-v2 antworten (Belege oben und
 *  frontend/lib/settings.ts VOICE_OPTIONS). Männer zuerst, die Vorgabe vorn. */
export const SCHMIED_STIMMEN: readonly { id: string; label: string; gender: "male" | "female" }[] = [
  { id: "soniox:Jonas", label: "Jonas", gender: "male" },
  { id: "soniox:Florian", label: "Florian", gender: "male" },
  { id: "soniox:Daniel", label: "Daniel", gender: "male" },
  { id: "soniox:Nathan", label: "Nathan", gender: "male" },
  { id: "soniox:Konrad", label: "Konrad", gender: "male" },
  { id: "soniox:Annika", label: "Annika", gender: "female" },
  { id: "soniox:Grace", label: "Grace", gender: "female" },
  { id: "soniox:Iris", label: "Iris", gender: "female" },
  { id: "soniox:Marlene", label: "Marlene", gender: "female" },
];

/** Der Schlüssel der gewählten Stimme in den Einstellungen des Kontos. */
export const SCHMIED_STIMME_FELD = "entwicklerStimme";

/** Die Vorgabe (Begründung oben). */
export const SCHMIED_STIMME_VORGABE = SCHMIED.stimmeVorgabe;

/** Sein Sprechtempo (1 = normal) — etwas schneller als normal, unabhängig von Vesperas Tempo-Regler (Begründung oben). */
export const SCHMIED_TEMPO = SCHMIED.tempo;

/** Die Stimme, die den Schmied liest: die gewählte, wenn sie in der Liste steht — sonst die Vorgabe. Rein. */
export function schmiedStimmeAus(gewaehlt: unknown): string {
  return typeof gewaehlt === "string" && SCHMIED_STIMMEN.some((s) => s.id === gewaehlt) ? gewaehlt : SCHMIED_STIMME_VORGABE;
}

/*
 * DIE STIMME JE ENTWICKLER (16.10.2026, Seite „Entwickler-Team“; Martin, 20:16: „Ja, du sollst bauen“ — Entscheidung 2):
 * auch der Zimmermann bekommt eine eigene Wahl (`zimmermannStimme`, shared/entwickler.ts `stimmeFeld`). Dieselbe Liste wie beim
 * Schmied (SCHMIED_STIMMEN) — eine Liste, eine Prüfung; ohne gültige Wahl gilt seine Vorgabe aus shared/entwickler.ts.
 * Nur Konto 1 wählt: für jedes andere Konto gilt immer die Vorgabe (`entwicklerStimmeFuerKonto`). Nie auf Zuruf
 * (GESPERRTE_FELDER „schutz“, wie `entwicklerStimme`).
 */

/** Ist das eine erlaubte gespeicherte Wahl — eine Stimme der Liste oder `null` (= Vorgabe)? Für Server-Filter und Gerät. Rein. */
export function stimmeWahlGueltig(wert: unknown): wert is string | null {
  return wert === null || (typeof wert === "string" && SCHMIED_STIMMEN.some((s) => s.id === wert));
}

/** Die Stimme eines Entwicklers: die gewählte, wenn sie in der Liste steht — sonst seine Vorgabe. Rein. */
export function entwicklerStimmeAus(id: EntwicklerId, gewaehlt: unknown): string {
  return typeof gewaehlt === "string" && stimmeWahlGueltig(gewaehlt) ? gewaehlt : entwickler(id).stimmeVorgabe;
}

/** Dasselbe mit dem Konto: nur Konto 1 hat eine Wahl, jedes andere Konto hört immer die Vorgabe. Rein. */
export function entwicklerStimmeFuerKonto(id: EntwicklerId, gewaehlt: unknown, userId: number | null | undefined): string {
  return entwicklerStimmeAus(id, entwicklerImChatFuerKonto(userId) ? gewaehlt : null);
}

/*
 * DAS SPRECHTEMPO JE ENTWICKLER (17.10.2026, Martin 15:06: „Sprechgeschwindigkeit der Systeme getrennt einstellen“):
 * `entwicklerTempo` (Schmied) und `zimmermannTempo` — Kontowerte, `null` = der bisherige Festwert (shared/entwickler.ts `tempo`).
 * Erlaubt sind 0,8 bis 1,6 in Schritten von 0,05; alles andere (Zahl daneben, Text, NaN) gilt nicht und fällt am Server weg.
 * Das Tempo selbst macht der vorhandene Weg (/api/voice-preview: ffmpeg atempo 0,5–2,0 oder Soniox `speed`); der Zwischenspeicher
 * führt es im Schlüssel (ttsCacheKey / cacheSchluessel). Nur Konto 1; nie auf Zuruf (GESPERRTE_FELDER „schutz“, wie die Stimme).
 */
export const ENTWICKLER_TEMPO_GRENZE = { min: 0.8, max: 1.6, schritt: 0.05 } as const;

/** Ist das ein erlaubter gespeicherter Wert — ein Tempo im Raster oder `null` (= Vorgabe)? Für Server-Filter und Gerät. Rein. */
export function tempoWahlGueltig(wert: unknown): wert is number | null {
  if (wert === null) return true;
  if (typeof wert !== "number" || !Number.isFinite(wert)) return false;
  const g = ENTWICKLER_TEMPO_GRENZE;
  if (wert < g.min - 1e-9 || wert > g.max + 1e-9) return false;
  const schritte = (wert - g.min) / g.schritt;
  return Math.abs(schritte - Math.round(schritte)) < 1e-6;
}

/** Das Tempo eines Entwicklers: das gewählte, wenn es gültig ist — sonst sein bisheriger Festwert. Rein. */
export function entwicklerTempoAus(id: EntwicklerId, gewaehlt: unknown): number {
  return typeof gewaehlt === "number" && tempoWahlGueltig(gewaehlt) ? gewaehlt : entwickler(id).tempo;
}

/** Dasselbe mit dem Konto: nur Konto 1 hat eine Wahl, jedes andere Konto hört immer das Festtempo. Rein. */
export function entwicklerTempoFuerKonto(id: EntwicklerId, gewaehlt: unknown, userId: number | null | undefined): number {
  return entwicklerTempoAus(id, entwicklerImChatFuerKonto(userId) ? gewaehlt : null);
}

/*
 * DIE AUSFÜHRLICHKEIT JE ENTWICKLER (17.10.2026): „kurz“ / „normal“ / „ausführlich“, Vorgabe normal (`null`). Gespeichert wird die
 * Kennung ohne Umlaut. Sie wirkt NUR als Anzeige: der Mithör-Kopf (scripts/dreier-hoeren.mjs, scripts/entwickler/zimmermann-hoeren.mjs)
 * druckt „Martins Wunsch zur Ausführlichkeit: …“ — DATEN, kein Auftrag. Nie auf Zuruf (GESPERRTE_FELDER „schutz“).
 */
export const AUSFUEHRLICHKEITEN = [
  { id: "kurz", label: "Kurz" },
  { id: "normal", label: "Normal" },
  { id: "ausfuehrlich", label: "Ausführlich" },
] as const;
export type Ausfuehrlichkeit = (typeof AUSFUEHRLICHKEITEN)[number]["id"];
export const AUSFUEHRLICHKEIT_VORGABE: Ausfuehrlichkeit = "normal";

/** Ist das ein erlaubter gespeicherter Wert — eine der drei Kennungen oder `null`? Rein. */
export function ausfuehrlichkeitWahlGueltig(wert: unknown): wert is Ausfuehrlichkeit | null {
  return wert === null || (typeof wert === "string" && AUSFUEHRLICHKEITEN.some((a) => a.id === wert));
}

/** Die Ausführlichkeit: die gewählte, wenn gültig — sonst „normal“. Rein. */
export function ausfuehrlichkeitAus(gewaehlt: unknown): Ausfuehrlichkeit {
  return typeof gewaehlt === "string" && ausfuehrlichkeitWahlGueltig(gewaehlt) ? gewaehlt : AUSFUEHRLICHKEIT_VORGABE;
}

/** So lange bleibt das Mikro nach einer Anrede an den Schmied zu, wenn kein Beitrag kommt. */
export const SCHMIED_WARTEN_MS = 20_000;

/** Nach einem Beitrag „ich arbeite noch“ (Feld `weiter`) bleibt das Mikro so lange zu, bevor der Rückfall öffnet (Martin nie endlos festhängen). */
export const SCHMIED_WEITER_MS = 5 * 60_000;

/*
 * 17.10.2026 (Martin 11:15/11:19): FRAGT der Schmied Martin, ohne dass etwas auf ihn wartet, öffnet das Mikro nach dem Vorlesen —
 * oder, wird er NICHT vorgelesen, nach einer Lesefrist ab der Anzeige (frontend/lib/schmied-warten.ts `frageKam`). Die Frist
 * richtet sich nach der Länge: SCHMIED_FRAGE_MS_JE_WORT je Wort (ruhiges Lesen, ~200 Wörter/min), mindestens
 * SCHMIED_FRAGE_LESEN_MIN_MS, höchstens SCHMIED_FRAGE_LESEN_MAX_MS.
 */
export const SCHMIED_FRAGE_LESEN_MIN_MS = 4_000;
export const SCHMIED_FRAGE_LESEN_MAX_MS = 20_000;
export const SCHMIED_FRAGE_MS_JE_WORT = 300;

/** Die Lesefrist eines nicht vorgelesenen Beitrags mit Frage (oben). Rein. */
export function schmiedFrageLeseMs(text: string): number {
  const woerter = (text ?? "").trim().split(/\s+/).filter(Boolean).length;
  return Math.min(SCHMIED_FRAGE_LESEN_MAX_MS, Math.max(SCHMIED_FRAGE_LESEN_MIN_MS, woerter * SCHMIED_FRAGE_MS_JE_WORT));
}

/**
 * Nach einem Beitrag AN VESPERA (Feld `an: "vespera"`) bleibt das Mikro zu, bis sie geantwortet hat — höchstens so lange ab dem
 * Ende seines Vorlesens (frontend/lib/schmied-warten.ts, „Halt an Vespera“). Danach die normale Regel (Martin nie endlos festhängen).
 */
export const SCHMIED_AN_VESPERA_MS = 60_000;

/*
 * DIE ANZEIGE „SCHMIED ARBEITET“ HÄLT DAS MIKRO (Martin, 16.10.2026, 18:29: „zum letzten Mal“). Steht beim Ablauf einer Frist
 * (SCHMIED_WARTEN_MS oder SCHMIED_WEITER_MS) die Warteblase „Schmied arbeitet / bereitet eine Antwort vor“ im Chat, öffnet das
 * Mikro NICHT, sondern prüft alle SCHMIED_STATUS_PRUEF_MS erneut (frontend/lib/schmied-warten.ts). Endet die Anzeige ohne
 * Beitrag, öffnet es nach SCHMIED_STATUS_SCHONFRIST_MS (ein Beitrag direkt nach dem „aus“ öffnet wie immer selbst); spätestens
 * SCHMIED_STATUS_GRENZE_MS nach dem Fristablauf öffnet es trotzdem — nie dauerhaft zu. Notschalter am Server:
 * `SCHMIED_STATUS_HAELT_MIKRO=0` (server/schmied-status.ts).
 */
export const SCHMIED_STATUS_PRUEF_MS = 1_000;
export const SCHMIED_STATUS_SCHONFRIST_MS = 5_000;
export const SCHMIED_STATUS_GRENZE_MS = 10 * 60_000;

/** Der Schlüssel „Schmied-Stimme im Ganz-Modus“ (17.10.2026): Beitrag erst ganz vertonen, dann sprechen. Vorgabe aus = stückweise. */
export const SCHMIED_GANZ_FELD = "schmiedStimmeGanz";

/** Ist „Schmied-Stimme im Ganz-Modus“ an? Nur Konto 1 und nur bei ausdrücklichem `true`. Rein. */
export function schmiedStimmeGanzAn(wert: unknown, userId: number | null | undefined): boolean {
  return entwicklerImChatFuerKonto(userId) && wert === true;
}

/** Der Schlüssel „Zimmermann und Hüter im Gespräch vorlesen“ (19.10.2026). */
export const ZUSATZ_VORLESEN_FELD = "zusatzVorlesen";

/**
 * Liest die App Beiträge von Zimmermann und Hüter von selbst vor, wenn Martin gerade mit ihnen spricht (Faden offen, Übergabe)?
 * Nur Konto 1; Vorgabe AN — fehlt der Wert, gilt an, nur ein ausdrückliches `false` (oder ein fremder Wert) schaltet aus. Rein.
 */
export function zusatzVorlesenAn(wert: unknown, userId: number | null | undefined): boolean {
  return entwicklerImChatFuerKonto(userId) && (wert === undefined || wert === null || wert === true);
}

/** Ist „Entwickler vorlesen“ an? Nur Konto 1 und nur bei ausdrücklichem `true`. Rein. */
export function entwicklerVorlesenAn(wert: unknown, userId: number | null | undefined): boolean {
  return entwicklerImChatFuerKonto(userId) && wert === true;
}

/*
 * DIE ANREDE
 *
 * ⚠️ `\b` kennt keine Umlaute (21.09.): die Grenze nach dem Namen ist „kein Buchstabe“ (`\p{L}`), sonst träfe „Schmiedel“,
 *    „Schmidtke“ oder „Schmiedeeisen“.
 *
 * DIE SCHREIBWEISEN (Martin, 22:38): die Spracherkennung schreibt „Schmied“ fast immer „Schmid“ oder „Schmidt“ („lieber
 * Schmid“). Bis dahin galt „Schmid“ bewusst NICHT — die Anrede wurde so nie erkannt. Jetzt gelten Schmied, Schmid, Schmidt
 * und Schmiet gleich (SCHMIED_RUFNAME), bei Martin wie bei Vespera. Der Preis: ein Herr Schmid, mit Komma angesprochen,
 * zählt auch — im Dreier-Modus (nur Konto 1, Schalter) ist das hinnehmbar.
 *
 * Als Anrede zählt der Name
 *   - am Satzanfang mit Satzzeichen danach („Schmied, du bist dran.“, „Hey Schmied: …“, „Schmied?“), oder
 *   - am Satzende nach einem Komma oder Strich („Was meinst du, Schmied?“, „Danke, Schmied.“), oder
 *   - am Satzende vor einem Frage- oder Rufzeichen („Hast du das gesehen Schmied?“) — außer nach einem Artikel
 *     („Fragen wir den Schmied?“ redet über ihn, nicht mit ihm).
 *
 * Welche Anrede gilt: die LETZTE im letzten Absatz, unter dessen letzten drei Sätzen. Steht dort „Martin, …“ nach
 * „Schmied, …“, ist Martin dran. Steht weiter vorn eine Anrede an den Schmied und am Schluss nichts, gilt das nicht —
 * die Antwort ist dann an Martin gerichtet (wie bei der Frage am Ende, frontend/lib/frage-am-ende.ts).
 */

const KEIN_BUCHSTABE = "(?!\\p{L})";
/** Alle Schreibweisen des Namens, wie die Spracherkennung sie liefert (oben). Längste zuerst. */
const SCHMIED_RUFNAME = SCHMIED.rufname;
/** Kein Buchstabe davor (statt `\b`). */
const KEIN_BUCHSTABE_DAVOR = "(?<!\\p{L})";
/** Das Satzzeichen nach dem Namen einer Anrede. Ein angeklebter Bindestrich ist ein Wort („Vespera-App“, „Schmied-Beitrag“),
 *  nur ein freistehender Strich („Schmied - schau mal“) zählt. (16.10.2026) */
const NACH_DEM_NAMEN = "(?:\\s*[,:!?—–]|\\s+-)";
const VORNE_SCHMUCK = "^[\\s\"'„“»«(*_]*";
const HINTEN_SCHMUCK = "[\\s\"'“”»«)*_]*$";
// „die“: „Fragen wir die Vespera?“ (16.10.2026, `anVesperaGerichtet` unten).
const ARTIKEL = /(?:^|\s)(?:der|den|dem|des|die|beim|zum|zur|vom|unser(?:em|en|e)?|ein(?:em|en|e)?)\s+$/iu;

/** Die letzten Sätze des letzten Absatzes (höchstens drei), ältester zuerst. */
function schlussSaetze(text: string): string[] {
  const absaetze = (text ?? "")
    .split(/\n\s*\n/)
    .map((a) => a.trim())
    .filter(Boolean);
  const letzter = absaetze.at(-1) ?? "";
  return letzter
    .split(/(?<=[.!?…])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(-3);
}

/** Ein Name als Muster: jeder Buchstabe wörtlich (Groß/klein egal). */
function namensMuster(name: string): string {
  return name.replace(/[^\p{L}]/gu, "");
}

/** Wird in diesem Satz der Name (Muster, ohne Gruppe drumherum nötig) angesprochen (Regeln oben)? Rein. */
export function redetAnMuster(satz: string, n: string): boolean {
  if (!n) return false;
  const vorne = new RegExp(`${VORNE_SCHMUCK}(?:(?:hey|hallo|hi|na|und|also|ok|okay|gut|danke|lieber)[\\s,]+)?${n}${KEIN_BUCHSTABE}${NACH_DEM_NAMEN}`, "iu");
  if (vorne.test(satz)) return true;
  const hintenKomma = new RegExp(`[,—–-]\\s*(?:lieber\\s+)?${n}${KEIN_BUCHSTABE}\\s*[.!?…]*${HINTEN_SCHMUCK}`, "iu");
  if (hintenKomma.test(satz)) return true;
  const hintenFrage = new RegExp(`(?:^|\\s)${n}${KEIN_BUCHSTABE}\\s*[?!]+${HINTEN_SCHMUCK}`, "iu");
  const m = hintenFrage.exec(satz);
  if (!m) return false;
  return !ARTIKEL.test(satz.slice(0, m.index + (/^\s/.test(m[0]) ? 1 : 0)));
}

/**
 * Wird in diesem Satz der Entwickler `id` angesprochen — mit allen Schreibweisen seines Rufnamens (shared/entwickler.ts)? Rein.
 * (16.10.2026: das alleinstehende „Zimmer“ zählt nie — der Rufname des Zimmermanns kennt nur ganze Namensformen.)
 */
export function redetEntwicklerAn(satz: string, id: EntwicklerId): boolean {
  return redetAnMuster(satz, entwickler(id).rufname);
}

/** Wird in diesem Satz `name` angesprochen (Regeln oben)? Rein. */
export function redetAn(satz: string, name: string): boolean {
  return redetAnMuster(satz, namensMuster(name));
}

/**
 * Spricht Vespera am Ende ihrer Antwort den Schmied an (und nicht danach wieder Martin)? Rein.
 * `false` heißt: Verhalten wie immer — das Mikro entscheidet wie bisher.
 */
export function sprichtSchmiedAn(text: string): boolean {
  return sprichtEntwicklerAn(text, "schmied");
}

/**
 * Spricht Vespera am Ende ihrer Antwort Zimmermann oder Hüter an — und NICHT zugleich den Schmied? (19.10.2026) Dann wartet das Auto-Mikro
 * auf IHN wie sonst auf den Schmied (frontend/app.ts). Wer beide anspricht, lässt es beim Schmied (wie bisher). Rein.
 */
export function zusatzAmEnde(text: string): ZusatzId | null {
  if (sprichtSchmiedAn(text)) return null;
  return ZUSATZ_IDS.find((id) => sprichtEntwicklerAn(text, id)) ?? null;
}

/**
 * Wie `sprichtSchmiedAn`, für jeden Entwickler (19.10.2026, Übergabe an Zimmermann/Hüter): spricht Vespera am Ende ihrer Antwort DIESEN
 * Entwickler an (seine Schreibweisen aus shared/entwickler.ts) und nicht danach wieder Martin? Rein.
 */
export function sprichtEntwicklerAn(text: string, id: EntwicklerId): boolean {
  const name = entwickler(id).rufname;
  const saetze = schlussSaetze(text);
  for (let i = saetze.length - 1; i >= 0; i--) {
    const s = saetze[i] ?? "";
    if (redetAn(s, "Martin")) return false;
    if (redetAnMuster(s, name)) return true;
  }
  return false;
}

/*
 * MARTIN SPRICHT DEN SCHMIED AN (Martin, 21:29)
 *
 * Beginnt Martins Nachricht mit „Hey Schmied“ oder „Schmied,“, antwortet Vespera NICHT: kein Modell-Zug, die Nachricht steht
 * im Verlauf, der Schmied hört sie über den Dreier-Strom, und das Auto-Mikro wartet auf seinen Beitrag (höchstens
 * SCHMIED_WARTEN_MS). Nur bei „Entwickler im Chat“ (server/dreier-modus.ts, `nachrichtAnSchmied`) — sonst bliebe Martin ohne
 * Antwort.
 *
 * Als Anrede zählt NUR der Anfang (alle Schreibweisen, SCHMIED_RUFNAME):
 *   - mit Gruß davor („Hey“, „Hallo“, „Hi“, „Okay“, „Lieber“ …) reicht ein Leerzeichen danach (die Erkennung setzt selten
 *     ein Komma);
 *   - ohne Gruß braucht es ein Satzzeichen („Schmid, …“, „Schmidt: …“) ODER danach einen typischen Anfang einer Rede an
 *     jemanden (ANREDE_ANFANG: „Schmid ich habe eine Frage“, „Schmidt kannst du …“). Sonst wäre „Schmied ist ein schöner
 *     Beruf“ eine Anrede — „ist“ steht darum nicht in der Liste.
 * Wortgrenze ohne `\b` (oben): „Schmiedel“, „Schmidtke“ nie.
 * ⚠️ Das Wach-Wort „Hey Vespera“ bleibt unberührt — es lauscht nativ auf seinen eigenen Satz, „Hey Schmied“ weckt nichts.
 */
// 20.10.2026 (Martin 10:03 „Lieber Zimmermann, wie weit …“): auch „Grüß dich“, „Guten Morgen/Tag/Abend“, „Moin“ sind Gruß vor dem Namen.
const GRUSS = "(?:hey|hallo|hi|he|okay|ok|servus|moin|gr(?:ü|ue|u)(?:ß|ss)\\s+dich|guten\\s+(?:morgen|tag|abend)|lieber|liebe)";
/**
 * Wörter, mit denen eine Rede an jemanden beginnt (Martin, 22:38) — nach dem Namen ohne Satzzeichen.
 * 18.10.2026 (Martin 13:27): „Zimmermann Frage, ich bin jetzt am Rechner …“ — „frage“ gehört dazu (Name vorn, ohne Komma).
 */
const ANREDE_ANFANG =
  "(?:ich|kannst|könntest|würdest|hast|bist|magst|willst|weißt|wie|was|wo|warum|wieso|weshalb|wann|wer|welche[rsnm]?|sag|sage|schau|schaust|bitte|du|wir|hör|hörst|kommst|komm|lieber|prüf|prüfe|mach|gib|zeig|hilf|danke|guck|check|frage(?=\\s*[,:]))";
const AN_SCHMIED_MIT_GRUSS = new RegExp(`${VORNE_SCHMUCK}${GRUSS}[\\s,!.]+${SCHMIED_RUFNAME}${KEIN_BUCHSTABE}`, "iu");
const AN_SCHMIED_OHNE_GRUSS = new RegExp(
  `${VORNE_SCHMUCK}${SCHMIED_RUFNAME}${KEIN_BUCHSTABE}(?:${NACH_DEM_NAMEN}|\\s+${ANREDE_ANFANG}${KEIN_BUCHSTABE})`,
  "iu",
);

/**
 * „An Schmid, bitte …“, „Ann Schmidt bitte“, „An den Schmied: …“ (16.10., 10:17): am Nachrichtenanfang ist „an“ (die Erkennung
 * schreibt auch „ann“/„anne“) vor dem Namen eine Anrede, mit oder ohne Satzzeichen. „Anna“/„Anja“ ist ein Vorname und passt
 * nicht; mitten im Satz („Ich schreibe an Schmied.“) gilt es nicht, denn die Regel hängt am Anfang.
 */
const AN_VORSATZ = "(?:an|ann|anne)(?:\\s+den)?[\\s,]+";
/** Wie `AN_SCHMIED_OHNE_GRUSS`, aber für „An <Name> …“ am Anfang — der Vorsatz ersetzt das Satzzeichen. */
function anVorsatzAmAnfang(t: string, name: string): boolean {
  return new RegExp(`${VORNE_SCHMUCK}${AN_VORSATZ}${name}${KEIN_BUCHSTABE}`, "iu").test(t);
}

/*
 * DER ANKÜNDIGUNGSSATZ (Martin, 16.10., Journal 09:49): „Ich möchte jetzt mit Schmid weiterreden …“ — kein Name am Anfang,
 * aber eindeutig an den Schmied gerichtet. Als Anrede zählt ein Satz dieser Form unter den ERSTEN ZWEI Sätzen der Nachricht,
 * und zwar nur als ganzer Satzanfang (kleiner Füllsatz wie „Also,“ davor erlaubt):
 *   - „[ich] möchte/will [gern] [jetzt] [wieder] mit [dem] Schmied [jetzt] [wieder] (weiter)reden/sprechen“,
 *   - „jetzt rede/spreche ich [wieder] mit [dem] Schmied“,
 *   - „an Schmied:“.
 * ⚠️ Es zählen nur die Schreibweisen von SCHMIED_RUFNAME, Wortgrenze ohne `\b` — „Schmiedel“ nie. „Vespera, sag Schmied, dass …“
 *    beginnt nicht so und ist darum KEINE Anrede (ein Satz, der Vespera zuerst anspricht, auch nicht).
 */
const FUELLWORT = "(?:(?:also|ok|okay|so|gut|na|und|dann|hey|hallo|nun|jetzt)[\\s,]+)?";
const MIT_SCHMIED = `mit\\s+(?:dem\\s+)?${SCHMIED_RUFNAME}${KEIN_BUCHSTABE}`;
const REDEN = "(?:weiter\\s*)?(?:reden|sprechen)";
const ANKUENDIGUNG = [
  `(?:ich\\s+)?(?:möchte|moechte|will)\\s+(?:gerne?\\s+)?(?:(?:jetzt|nun|gleich|wieder)\\s+)*${MIT_SCHMIED}(?:\\s+(?:(?:jetzt|nun|gleich|wieder)\\s+)*)?${REDEN}${KEIN_BUCHSTABE}`,
  `jetzt\\s+(?:rede|spreche)\\s+ich\\s+(?:wieder\\s+)?${MIT_SCHMIED}`,
  `an\\s+${SCHMIED_RUFNAME}${KEIN_BUCHSTABE}\\s*:`,
].map((m) => new RegExp(`${VORNE_SCHMUCK}${FUELLWORT}${m}`, "iu"));

/*
 * AUSDRÜCKLICHE ZUORDNUNG ÜBERALL (Martin, 16.10., gesprochen): auch MITTEN im Text oder am ENDE zählt „diese Nachricht geht an
 * Schmied“, „das geht jetzt an Schmied“, „das ist für Schmied“, „das richtet sich an Schmied“ — Subjekt (diese/die/meine Nachricht,
 * Frage, das, dies, es, alles) + geht/ist/richtet sich + an/für [den] Schmied. Ohne Subjekt nie („das Geschenk ist für Schmied“),
 * „Schmied hat gesagt …“ und „Vespera, sag Schmied, dass …“ ebenso nicht.
 */
const ZUORDNUNG = new RegExp(
  `${KEIN_BUCHSTABE_DAVOR}(?:(?:diese|die|meine)\\s+(?:nachricht|frage)|das|dies|es|alles)\\s+(?:(?:geht|ist)|richtet\\s+sich)\\s+(?:(?:jetzt|nun|hier|nur|ausdrücklich)\\s+)*(?:an|für)\\s+(?:den\\s+)?${SCHMIED_RUFNAME}${KEIN_BUCHSTABE}`,
  "iu",
);

function ersteSaetze(text: string): string[] {
  return (text ?? "")
    .trim()
    .split(/(?<=[.!?…])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 2);
}

/**
 * Sieht der Anfang nach EINGEFÜGTEM oder diktiertem Fremdtext aus statt nach einer Anrede (Prüfung 17.10.2026)? Dann gilt der Name vorn
 * NICHT als Anrede — Vespera antwortet wie bisher (gilt für Schmied und Zimmermann):
 *   - ein Anführungszeichen vorn („Zimmermann, ich kündige Ihnen …“ ist ein Zitat);
 *   - Briefanrede „Lieber/Liebe <Name> …“ (eine Mail, kein Zuruf; „Hey/Hallo <Name>“ bleibt eine Anrede);
 *   - „<Name>: …“ ohne Anrede-Wort (Listen-/Formularform);
 *   - „<Name>, … ; …“ mit Strichpunkt in der ersten Zeile (Tabellenzeile, „Zimmermann, Klaus; 3 Stunden; 240 Euro“).
 * Eine echte Anrede („Hey Zimmermann, wie ist die Lage“, „Zimmermann, kurze Frage …“) bleibt erkannt. Rein.
 */
function einschleusungsForm(t: string, rufname: string): boolean {
  if (/^\s*["'„“»«]/u.test(t)) return true;
  // Briefanrede und „Name:“ gelten nur dann NICHT, wenn danach keine typische Rede an jemanden folgt („lieber Schmid ich habe eine Frage“,
  // „Schmied: hörst du mit?“ bleiben Anreden — so spricht Martin; „Lieber Zimmermann, anbei die Rechnung“ und „Zimmermann: Termin“ nicht).
  const redeDanach = `(?:\\s*[,:])?\\s+${ANREDE_ANFANG}${KEIN_BUCHSTABE}`;
  const brief = new RegExp(`^[\\s(*_]*(?:lieber|liebe)[\\s,]+${rufname}${KEIN_BUCHSTABE}`, "iu");
  const mitRede = new RegExp(`${brief.source}${redeDanach}`, "iu");
  if (brief.test(t) && !mitRede.test(t)) return true;
  const doppelpunkt = new RegExp(`^[\\s(*_]*${rufname}${KEIN_BUCHSTABE}\\s*:`, "iu");
  const doppelpunktRede = new RegExp(`${doppelpunkt.source}\\s*${ANREDE_ANFANG}${KEIN_BUCHSTABE}`, "iu");
  if (doppelpunkt.test(t) && !doppelpunktRede.test(t)) return true;
  return new RegExp(`^[\\s(*_]*${rufname}${KEIN_BUCHSTABE}\\s*,[^\\n]*;`, "iu").test(t);
}

/** Beginnt Martins Nachricht mit einer Anrede an den Schmied (Name am Anfang oder Ankündigungssatz in den ersten zwei Sätzen)? Rein. */
export function anSchmiedGerichtet(nachricht: string): boolean {
  const t = (nachricht ?? "").trimStart();
  if (!einschleusungsForm(t, SCHMIED_RUFNAME) && (AN_SCHMIED_MIT_GRUSS.test(t) || AN_SCHMIED_OHNE_GRUSS.test(t) || anVorsatzAmAnfang(t, SCHMIED_RUFNAME))) return true;
  return ZUORDNUNG.test(t) || ersteSaetze(t).some((satz) => ANKUENDIGUNG.some((re) => re.test(satz)));
}

/*
 * MARTIN SPRICHT DEN ZIMMERMANN AN (17.10.2026, Martin 15:08: „Hey Zimmermann, das betrifft dich“)
 *
 * Wie beim Schmied zählt NUR der Anfang der Nachricht — „Hey Zimmermann …“, „Zimmermann, …“, „Zimmermann kannst du …“, „An Zimmermann: …“
 * (mit den Schreibweisen der Spracherkennung, shared/entwickler.ts `rufname`). Kein Faden, keine Ankündigungssätze, keine Zuordnung
 * mitten im Satz: der Zimmermann bekommt eine Nachricht nur auf eine ausdrückliche Anrede oder Martins Wahl „An: Zimmermann“ —
 * im Zweifel antwortet Vespera. Das alleinstehende „Zimmer“ ist nie eine Anrede (der Rufname kennt nur ganze Namensformen).
 */
/**
 * Die Anrede-Namen des Zimmermanns: sein Rufname plus sein Rechner — „Server 6“, „Server sechs“, „Server6“, „S6“ (Martin, 17.10.2026:
 * „Wenn ich Zimmermann oder Server 6 anspreche, dann soll nicht der Schmied kommen“). „Server 8“ gehört nicht dazu. Nur für die ANREDE
 * am Anfang; mitten im Satz („Wie ist die Lage auf Server 6?“) ist es kein Name und bleibt ohne Wirkung.
 */
/*
 * 17.10.2026 (Martin 19:46): der HÜTER (Server 7) wird genauso angesprochen — „Hey Hüter …“, „Hüter, …“, „Server 7, kannst du …“, „S7 …“.
 * Darum gilt alles hier für JEDEN Zusatz-Entwickler (shared/entwickler.ts `ZUSATZ_IDS`, Name `rufname`, Rechner `rechner`); die Muster
 * werden je Kennung einmal gebaut. „hüten“, „Hüte“, „Torhüter“ sind nie eine Anrede (Wortgrenze ohne `\b`, nur ganze Namensformen).
 */
/**
 * OHNE Gruß zählt der Rechner-Name („Server 6“, „S6“) nur, wenn danach eine echte Rede an jemanden folgt — Martin redet über Server 6
 * auch mit dem Schmied („Server 6, der ist wieder oben“, „Server 6 wo liegt das Backup“ sind Themensätze, keine Anrede; Prüfung 17.10.).
 * Darum die strenge Liste: ohne die Fragewörter wo/was/warum/wieso/weshalb/wann/wer/welche (nur „wie“ bleibt: „Server 6, wie ist die Lage“).
 * Mit Gruß („Hey S6 schau mal“) oder „An Server 6:“ ist es ausdrücklich eine Anrede. „Zimmermann, …“ bleibt unverändert.
 */
const REDE_AN_RECHNER =
  "(?:ich|kannst|könntest|würdest|hast|bist|magst|willst|weißt|wie|sag|sage|schau|schaust|bitte|du|wir|hör|hörst|kommst|komm|prüf|prüfe|mach|gib|zeig|hilf|danke|guck|check)";
interface ZusatzAnrede {
  rufname: string;
  mitGruss: RegExp;
  ohneGruss: RegExp;
}
const ZUSATZ_ANREDE = new Map<ZusatzId, ZusatzAnrede>();
function zusatzAnrede(id: ZusatzId): ZusatzAnrede {
  const da = ZUSATZ_ANREDE.get(id);
  if (da) return da;
  const e = entwickler(id);
  const name = e.rufname;
  const rechner = e.rechner;
  const rufname = rechner ? `(?:${name}|${rechner})` : name;
  const neu: ZusatzAnrede = {
    rufname,
    mitGruss: new RegExp(`${VORNE_SCHMUCK}${GRUSS}[\\s,!.]+${rufname}${KEIN_BUCHSTABE}`, "iu"),
    ohneGruss: new RegExp(
      `${VORNE_SCHMUCK}(?:${name}${KEIN_BUCHSTABE}(?:${NACH_DEM_NAMEN}|\\s+${ANREDE_ANFANG}${KEIN_BUCHSTABE})` +
        (rechner ? `|${rechner}${KEIN_BUCHSTABE}(?:\\s*[,:]|\\s+-)?\\s+${REDE_AN_RECHNER}${KEIN_BUCHSTABE}` : "") +
        ")",
      "iu",
    ),
  };
  ZUSATZ_ANREDE.set(id, neu);
  return neu;
}

/** Beginnt Martins Nachricht mit einer Anrede an diesen Zusatz-Entwickler (Name/Rechner am Anfang, „Hey … <Name>“, „An <Name>“)? Rein. */
export function anZusatzGerichtet(nachricht: string, id: ZusatzId): boolean {
  const t = (nachricht ?? "").trimStart();
  const a = zusatzAnrede(id);
  if (einschleusungsForm(t, a.rufname)) return false;
  return a.mitGruss.test(t) || a.ohneGruss.test(t) || anVorsatzAmAnfang(t, a.rufname);
}

/** Beginnt Martins Nachricht mit einer Anrede an den Zimmermann (Name am Anfang, „Hey/Hallo … Zimmermann“, „An Zimmermann“)? Rein. */
export function anZimmermannGerichtet(nachricht: string): boolean {
  return anZusatzGerichtet(nachricht, "zimmermann");
}

/*
 * „HEY VESPERA, SCHMIED, …“ (Martin 18.10.2026 10:51): ein Weckruf („Hey/Hallo/Hi/Ok/Okay Vespera“, mit oder ohne Komma) AM ANFANG,
 * unmittelbar gefolgt von einer zweiten Anrede mit dem NAMEN am Satzanfang (Schmied/Schmid/Schmidt/Schmiet, Zimmermann, Hüter — auch
 * „Hey Schmied …“) ist nur der Weckruf; die zweite Anrede gilt, als stünde sie allein am Anfang. Die EINE Stelle dafür.
 * Nicht betroffen: „Hey Vespera, wie ist das Wetter?“ (keine zweite Anrede), „Vespera, sag dem Schmied …“ (Ankündigung/Zuordnung zählen
 * hier nicht), „Hey Vespera, ich habe mit dem Schmied geredet“ (Name im Satz). Rein.
 */
const WECKRUF = new RegExp(`${VORNE_SCHMUCK}(?:hey|hallo|hi|he|ok|okay)[\\s,!.]+vespera${KEIN_BUCHSTABE}\\s*[,!.:]?\\s+`, "iu");

/*
 * NACH DEM WECKRUF (Martin 19.10.2026, seit Stunden derselbe Fehler): „Hey Vespera, ähm, Zimmermann, ich möchte …“ und
 * „Hey Vespera, Zimmerer, …“ (Spracherkennung) gingen an Vespera. Direkt nach dem Weckruf — und NUR dort — werden
 *   (a) Füllwörter (ähm, äh, also, ja, okay, und, na …) übersprungen und
 *   (b) die bekannten Verhörer der Namen mit folgendem Komma/Doppelpunkt/Gedankenstrich durch den Namen ersetzt.
 * Mitten im Satz oder ohne Satzzeichen danach zählt nichts davon („Der Zimmerer hat gebaut“, „Hey Vespera, Zimmerer bauen Häuser“). Rein.
 */
const FUELLWORT_NACH_WECKRUF = /^(?:(?:ähm|aehm|ehm|äh|aeh|eh|hm+|also|ja|okay|ok|und|na|nun|dann|so)(?!\p{L})[\s,.!:\u2013-]*)+/iu;
/**
 * Verhörer, die auch ein ALLTAGSWORT oder ein Nachname sind (Wächter, Güter, Pendler, Zimmerer), zählen nur, wenn der Rest des Satzes nach
 * Arbeit klingt — NUR Technik-/Server-/Entwicklerwörter (Server, Sicherung, Backup, Panel, Seite, Push, Commit, Ticket, Bericht, Entwickler …).
 * Höflichkeitswörter und Du-Anreden allein („bitte“, „sag mir“, „Stand“, „kannst du“) reichen NICHT: „Hey Vespera, Güter, was kostet der Transport, bitte“,
 * „Güter, Stand meiner Bestellung“, „Pendler: wie voll ist die A1, sag mir“, „Wächter, ich komme morgen vorbei“ bleiben bei Vespera.
 * „Schmitt“ ist NICHT aufgenommen (echter Nachname). „Schnitt“ (→ Schmied) gilt NUR in `verhoerteAnrede` unten (Schatten der neuen Weiche,
 * Vorgabe E7) und dort wie Wächter/Güter nur mit Arbeitswort im Rest; nach dem Weckruf der heutigen Weiche zählt er nicht.
 */
const ARBEIT_NACH_NAME =
  /(?<!\p{L})(?:server|sicherung\w*|backup\w*|panel|webseite|website|seite|deploy\w*|ticket\w*|build|push|short|commit\w*|code|newsletter|festplatte|speicher\w*|bericht\w*|entwickler\w*|lage\s+(?:der|bei\s+den)\s+entwickler\w*)(?!\p{L})/iu;
const VERHOERER_NACH_WECKRUF: ReadonlyArray<readonly [RegExp, string, boolean]> = [
  [/^(?:zimmer\s+mann|zimmerman|zimmermänner)(?=\s*[,:;\u2013-])/iu, "Zimmermann", false],
  [/^(?:zimmerer|pendler)(?=\s*[,:;\u2013-])/iu, "Zimmermann", true],
  [/^(?:hütter|huetter|hüther|huether)(?=\s*[,:;\u2013-])/iu, "Hüter", false],
  [/^(?:wächter|waechter|güter|gueter)(?=\s*[,:;\u2013-])/iu, "Hüter", true],
];

function verhoererNachWeckruf(rest: string): string {
  const ohneFuell = rest.replace(FUELLWORT_NACH_WECKRUF, "");
  if (!ohneFuell) return rest;
  for (const [muster, name, mitArbeit] of VERHOERER_NACH_WECKRUF) {
    const m = muster.exec(ohneFuell);
    if (!m) continue;
    if (mitArbeit && !ARBEIT_NACH_NAME.test(ohneFuell.slice(m[0].length))) continue;
    return name + ohneFuell.slice(m[0].length);
  }
  return ohneFuell;
}

/*
 * VERHÖRTE NAMEN AM ANFANG (19.10.2026, Plan „Die EINE Weiche“, Vorgabe E7 — vorerst NUR für den Schatten der neuen Weiche,
 * server/adressat-schatten.ts; die heutige Weiche nutzt diese Funktion nicht). „Schnitt“ → Schmied, „Zimmerer“/„Pendler“ → Zimmermann,
 * „Wächter“/„Güter“ → Hüter (dazu die übrigen Verhörer oben) — NUR als Anrede-Form am Anfang: der Name (höchstens nach Gruß oder „An“)
 * mit Komma/Doppelpunkt/Strichpunkt/Gedankenstrich danach. Wörter, die auch Alltag oder Nachname sind (Schnitt, Wächter, Güter, Pendler,
 * Zimmerer), brauchen wie nach dem Weckruf ein Arbeitswort im Rest (`ARBEIT_NACH_NAME`). Mitten im Satz, ohne Satzzeichen danach oder in Anführungszeichen zählt nichts
 * („Der Schnitt war gut“, „Wächter am Tor“, „Schnitt ist wichtig“). Rückgabe: der Satz mit dem richtigen Namen — die Anrede-Prüfung
 * (`anSchmiedGerichtet`, `anZusatzGerichtet`) entscheidet danach wie immer; ohne Treffer `null`. Rein.
 */
const VERHOERER_AM_ANFANG: ReadonlyArray<readonly [RegExp, string, boolean]> = [
  [/^schnitt(?=\s*[,:;–-])/iu, "Schmied", true],
  ...VERHOERER_NACH_WECKRUF,
];
const VORSATZ_VERHOERER = new RegExp(`^[\\s(*_]*(?:(?:${GRUSS}|an)[\\s,!.]+)?`, "iu");

export function verhoerteAnrede(nachricht: string): string | null {
  const t = (nachricht ?? "").trimStart();
  if (/^["'„“»«]/u.test(t)) return null;
  const vorsatz = VORSATZ_VERHOERER.exec(t)?.[0] ?? "";
  const rest = t.slice(vorsatz.length);
  for (const [muster, name, mitArbeit] of VERHOERER_AM_ANFANG) {
    const m = muster.exec(rest);
    if (!m) continue;
    const danach = rest.slice(m[0].length);
    if (mitArbeit && !ARBEIT_NACH_NAME.test(danach)) continue;
    return vorsatz + name + danach;
  }
  return null;
}

/*
 * GRUSS VOR DEM ENTWICKLERNAMEN IST KEIN PRIVATWORT (20.10.2026, Martin 10:03: „Lieber Zimmermann, wie weit sind wir …“ → Vespera,
 * `grund=sperre-privat` — die Privatliste traf „lieb…“ im Gruß). Für die PRIVAT-Prüfung fällt NUR der eine Gruß am Anfang weg, wenn
 * unmittelbar ein Entwicklername folgt (Rufname, Rechner, bekannte Verhörer; auch nach dem Weckruf „Hey Vespera,“). Der Rest der Nachricht
 * — Name und alles danach — wird unverändert geprüft: „Lieber Zimmermann, mein Arzttermin …“ bleibt privat, „ich liebe …“ ebenso.
 * Ein Anführungszeichen vorn (Zitat) schält nichts ab. Ob es eine Anrede ist, entscheidet weiter allein `anZusatzGerichtet`/`anSchmiedGerichtet`. Rein.
 */
const ENTWICKLER_NAME_VORN = `(?:${[SCHMIED_RUFNAME, ...ZUSATZ_IDS.map((id) => zusatzAnrede(id).rufname), "schnitt", "zimmerer", "pendler", "zimmermänner", "huetter", "huether", "wächter", "waechter", "güter", "gueter"].join("|")})`;
// „Lieber/Liebe“ nur mit Leerzeichen direkt vor dem Namen (Sicherheitsprüfung 20.10.): „Liebe, Zimmermann, ist das Schönste“ ist kein Gruß.
const GRUSS_VOR_ENTWICKLER = new RegExp(
  `^[\\s(*_]*(?:(?:lieber|liebe)\\s+|(?!lieb)${GRUSS}[\\s,!.]+)(?=${ENTWICKLER_NAME_VORN}${KEIN_BUCHSTABE})`,
  "iu",
);

export function ohneGrussVorEntwickler(nachricht: string): string {
  const t = nachricht ?? "";
  const weck = WECKRUF.exec(t)?.[0] ?? "";
  const rest = t.slice(weck.length);
  const m = GRUSS_VOR_ENTWICKLER.exec(rest);
  return m ? weck + rest.slice(m[0].length) : t;
}

/*
 * GESPROCHENE BRIEFANREDE (Vorfall 20.10.2026 21:51: „lieber Zimmermann, … meldet sich nicht. Warum?“ per Sprechtaste → keine Anrede).
 * `einschleusungsForm` hält „Lieber/Liebe <Name>, <Aussage>“ für eine eingefügte Mail — gesprochen ist es Martins Zuruf. NUR für die
 * Sprechtaste: „Lieber/Liebe“ direkt (nur Leerraum) vor einem Entwicklernamen am Anfang wird zu „Hallo“; ob es eine Anrede ist, entscheidet
 * danach unverändert die Anrede-Prüfung (Verhörer weiter nur mit Arbeitswort). Zitat vorn, anderer Name, Name mitten im Satz: unverändert. Rein.
 */
const BRIEF_ANREDE_VOR_ENTWICKLER = new RegExp(`^([\\s(*_]*)(?:lieber|liebe)\\s+(?=${ENTWICKLER_NAME_VORN}${KEIN_BUCHSTABE})`, "iu");
export function gesprocheneBriefAnrede(nachricht: string): string {
  const t = nachricht ?? "";
  return t.replace(BRIEF_ANREDE_VOR_ENTWICKLER, "$1Hallo ");
}

/** Der Satz nach einem Weckruf „Hey Vespera,“ — gleich welcher Art (auch ohne Entwicklername); ohne Weckruf am Anfang `null`. Rein. */
export function nachWeckruf(nachricht: string): string | null {
  const t = (nachricht ?? "").trimStart();
  const m = WECKRUF.exec(t);
  if (!m) return null;
  return t.slice(m[0].length).trim() || null;
}

/** Der Satz nach einem Weckruf „Hey Vespera,“ — wenn dort eine Anrede mit Namen an einen Entwickler beginnt; sonst `null`. Rein. */
export function ohneWeckruf(nachricht: string): string | null {
  const t = (nachricht ?? "").trimStart();
  const m = WECKRUF.exec(t);
  if (!m) return null;
  const rest = verhoererNachWeckruf(t.slice(m[0].length).trim());
  if (!rest) return null;
  const anSchmied =
    !einschleusungsForm(rest, SCHMIED_RUFNAME) && (AN_SCHMIED_MIT_GRUSS.test(rest) || AN_SCHMIED_OHNE_GRUSS.test(rest) || anVorsatzAmAnfang(rest, SCHMIED_RUFNAME));
  return anSchmied || ZUSATZ_IDS.some((id) => anZusatzGerichtet(rest, id)) ? rest : null;
}

/** Beginnt Martins Nachricht mit einer Anrede an den Hüter („Hey Hüter …“, „Hüter, …“, „Server 7, kannst du …“, „An S7: …“)? Rein. */
export function anHueterGerichtet(nachricht: string): boolean {
  return anZusatzGerichtet(nachricht, "hueter");
}

/*
 * ANREDE AN JEDEM SATZANFANG (Vorfall 21.10.2026 18:57, Sprechtaste: „Ok, super. An Schmid bitte die Breite für die Webseite …“ ging an den
 * Zimmermann — `anSchmiedGerichtet` prüft nur den Anfang der NACHRICHT, „Ok, super.“ stand davor; das Thema „Webseite“ gewann).
 * Hier zählt dieselbe Anrede-Prüfung (`anSchmiedGerichtet`, `anZusatzGerichtet`, Verhörer `verhoerteAnrede`) an JEDEM Satzanfang, auch nach
 * Füllwörtern („Ok“, „Also“, „Bitte“, „Jetzt“, „Und“ …) und nach dem Weckruf „Hey Vespera,“. Mitten im Satz nur „… noch was an <Name>: …“.
 * Auflagen Sicherheitsprüfer 21.10.2026:
 *   A2 „Ok/Okay“ ist hier KEIN Gruß, nur Füllwort: „Ok, Schmied hat gesagt …“ bleibt Erwähnung.
 *   A3 Schmid/Schmidt/Schmiet und die Verhörer Schmitt/Schmiedt/Schnitt zählen nach einem bloßen Gruß NIE („Hallo Schmidt, komm rein“,
 *      „Servus Schmid, magst an Kaffee?“, „Hey Schnitt …“) — nach Gruß nur der Name „Schmied“ selbst. Schmitt/Schmiedt/Schnitt nur nach „An“.
 *      Mensch-Zusatz nach dem Namen („Schmidt, der Hausarzt, …“, „An Schmitt, den Nachbarn, …“, „… komm rein“): keine Anrede, für alle Namen.
 *   A1 eindeutig: genau EIN Entwickler angeredet, kein ANDERER Entwickler und keine Vespera im Text (außer dem Weckruf vorn), keine Korrektur
 *      („nein“, „Moment“, „eigentlich“, „lieber an“, „doch an“, „nicht an/der …“) — sonst `null`, und die KI entscheidet allein.
 * Wer das nutzt, entscheidet nur zwischen Entwicklern (server/weiche/entscheide.ts Stufe 8a). Rein.
 */
const FUELLWOERTER = "(?:ok|okay|super|also|bitte|hey|jetzt|und|gut|so|na|nun|dann|ja|ähm|aehm|äh|aeh)";
const SATZ_FUELLER = new RegExp(`^(?:${FUELLWOERTER}${KEIN_BUCHSTABE}[\\s,.!:–-]*)+`, "iu");
const BEGINNT_MIT_OK = /^[\s(*_]*(?:ok|okay)(?!\p{L})/iu;
/** Gruß ohne „ok/okay“ (A2). */
const GRUSS_ECHT = "(?:hey|hallo|hi|he|servus|moin|gr(?:ü|ue|u)(?:ß|ss)\\s+dich|guten\\s+(?:morgen|tag|abend)|lieber|liebe)";
const SCHMIED_NICHT_GENAU = "(?:schmid|schmidt|schmiet|schmitt|schmiedt|schnitt)";
/** A3: Gruß + eine andere Schreibweise als „Schmied“ (auch nach Füllwörtern) — dann zählt in diesem Satz kein Schmied. */
const GRUSS_MIT_VERHOERER = new RegExp(`^[\\s(*_]*(?:${FUELLWOERTER}${KEIN_BUCHSTABE}[\\s,.!:–-]*)*${GRUSS_ECHT}[\\s,!.]+${SCHMIED_NICHT_GENAU}${KEIN_BUCHSTABE}`, "iu");
const SCHMIED_VERHOERT_NACH_AN = new RegExp(
  `^([\\s(*_]*(?:an|ann|anne)(?:\\s+den)?[\\s,!.]+)(?:schmitt|schmiedt|schnitt)(?=\\s*[,:;!?–-]|\\s+${ANREDE_ANFANG}${KEIN_BUCHSTABE})`,
  "iu",
);
const SATZ_GRENZE = /(?<=[.!?…])\s+|\n+/u;

/** Alle Namensformen je Entwickler (Rufname, Rechner, Verhörer) — für „nennt einen anderen“ (A1). */
const NAMEN_JE_ID: Readonly<Record<EntwicklerId, string>> = {
  schmied: `(?:${SCHMIED_RUFNAME}|schmitt|schmiedt|schnitt)`,
  zimmermann: `(?:${zusatzAnrede("zimmermann").rufname}|zimmerer|pendler|zimmermänner)`,
  hueter: `(?:${zusatzAnrede("hueter").rufname}|wächter|waechter|güter|gueter|huetter|huether)`,
};
/**
 * A3: Mensch-Zusatz direkt nach dem Namen am Satzanfang — dann ist es keine Anrede an einen Entwickler (Nachprüfung 21.10.2026):
 *   - Apposition mit Artikel („Schmidt, der Hausarzt, …“, „An Schmitt, den Nachbarn, …“);
 *   - Besitzwort oder Titel/Verwandtschaft („Schmid, mein Nachbar, …“, „An Schmid, Herrn Doktor, …“, „…, Dr. …“, „…, Onkel …“);
 *   - „du/ihr <Wort>[ <Wort>],“ („Schmidt, du alter Gauner, …“);
 *   - jedes „komm/kommt/kommen/kommst“ gleich nach dem Namen („Schmid, komm runter!“, „Hallo Schmidt, komm rein“);
 *   - die Sie-Form irgendwo danach im Satz („Schmid, bitte melden Sie sich beim Amt.“) — Martin siezt die Entwickler nie.
 * Randfall (nur notiert): „Schmied, der Schmied vom Dorf, …“ gilt weiter als Anrede (die Apposition mit Artikel zählt nur mit EINEM Wort).
 */
const NAME_AM_SATZANFANG = new RegExp(
  `^[\\s(*_]*(?:(?:${GRUSS}|an|ann|anne)(?:\\s+den)?[\\s,!.]+)?(?:${Object.values(NAMEN_JE_ID).join("|")})${KEIN_BUCHSTABE}`,
  "iu",
);
const MENSCH_NACH_NAME = new RegExp(
  `^(?:\\s*,\\s*(?:(?:der|die|das|den|dem)\\s+[\\p{L}-]+\\s*,|(?:mein|dein|unser|ihr|sein|euer|eure)\\p{L}*${KEIN_BUCHSTABE}|` +
    `(?:herr|herrn|frau|doktor|onkel|tante|opa|oma)${KEIN_BUCHSTABE}|(?:dr|prof)(?:\\.|${KEIN_BUCHSTABE})|(?:du|ihr)\\s+[\\p{L}-]+(?:\\s+[\\p{L}-]+)?\\s*,)|` +
    `[\\s,!]+(?:komm|kommt|kommen|kommst)${KEIN_BUCHSTABE})`,
  "iu",
);
/** Die Sie-Form (groß geschrieben, Groß/klein zählt hier). */
const SIE_FORM = /(?<!\p{L})(?:Sie|Ihnen)(?!\p{L})/u;

function menschNachName(satz: string): boolean {
  const m = NAME_AM_SATZANFANG.exec(satz);
  if (!m) return false;
  const rest = satz.slice(m[0].length);
  return MENSCH_NACH_NAME.test(rest) || SIE_FORM.test(rest);
}
const KORREKTUR = new RegExp(
  `${KEIN_BUCHSTABE_DAVOR}(?:nein|moment|eigentlich|lieber\\s+an|doch\\s+(?:an|lieber|nicht)|nicht\\s+(?:an|der|dem|den|zum|zur))${KEIN_BUCHSTABE}`,
  "iu",
);

function nochWasAn(rufname: string): RegExp {
  return new RegExp(`${KEIN_BUCHSTABE_DAVOR}noch\\s+(?:was|etwas|eins|eine\\s+sache)\\s+an\\s+(?:den\\s+)?${rufname}${KEIN_BUCHSTABE}\\s*[:–-]`, "iu");
}

/** Wer wird am Anfang DIESES Satzes angeredet (mit oder ohne Füllwörter davor)? Rein. */
function angeredetAmSatzanfang(satz: string): EntwicklerId[] {
  const roh = satz.trim();
  const ohneFueller = roh.replace(SATZ_FUELLER, "");
  if (menschNachName(roh) || menschNachName(ohneFueller)) return [];
  const schmiedErlaubt = !GRUSS_MIT_VERHOERER.test(roh);
  const treffer = new Set<EntwicklerId>();
  const formen = BEGINNT_MIT_OK.test(roh) ? [ohneFueller] : [roh, ohneFueller];
  for (const s of new Set(formen)) {
    const mitName = s.replace(SCHMIED_VERHOERT_NACH_AN, "$1Schmied");
    const t = verhoerteAnrede(mitName) ?? mitName;
    if (schmiedErlaubt && anSchmiedGerichtet(t)) treffer.add("schmied");
    for (const id of ZUSATZ_IDS) if (anZusatzGerichtet(t, id)) treffer.add(id);
  }
  return [...treffer];
}

/** Alle an einem Satzanfang (oder „noch was an <Name>:“) angeredeten Entwickler — ohne die Eindeutigkeit aus A1. Rein. */
export function entwicklerAngeredet(nachricht: string): EntwicklerId[] {
  const t = nachWeckruf(nachricht) ?? String(nachricht ?? "");
  const treffer = new Set<EntwicklerId>(t.split(SATZ_GRENZE).flatMap(angeredetAmSatzanfang));
  if (nochWasAn(SCHMIED_RUFNAME).test(t)) treffer.add("schmied");
  for (const id of ZUSATZ_IDS) if (nochWasAn(entwickler(id).rufname).test(t)) treffer.add(id);
  return [...treffer];
}

/** Der EINE eindeutig angeredete Entwickler (A1: kein anderer Entwickler, keine Vespera nach dem Weckruf, keine Korrektur); sonst `null`. Rein. */
export function eindeutigAngeredet(nachricht: string): EntwicklerId | null {
  const [einer, ...mehr] = entwicklerAngeredet(nachricht);
  if (!einer || mehr.length > 0) return null;
  const t = nachWeckruf(nachricht) ?? String(nachricht ?? "");
  if (KORREKTUR.test(t) || /(?<!\p{L})vespera(?!\p{L})/iu.test(t)) return null;
  const andere = (Object.keys(NAMEN_JE_ID) as EntwicklerId[]).filter((id) => id !== einer);
  return andere.some((id) => new RegExp(`${KEIN_BUCHSTABE_DAVOR}${NAMEN_JE_ID[id]}${KEIN_BUCHSTABE}`, "iu").test(t)) ? null : einer;
}

/*
 * MARTIN HOLT VESPERA DAZU (16.10.2026, Faden zum Schmied, server/dreier-modus.ts)
 *
 * Bis hierher beendete JEDES „Vespera“ im Text den Faden zum Schmied (`nenntVespera`). Am 16.10. 09:01 erzählte Martin dem
 * Schmied etwas ÜBER Vespera (Name in Satz 5 von 8, keine Anrede) — der Faden riss, Vespera antwortete, und weil ihre Antwort
 * den Faden löschte, ging auch seine nächste Nachricht an sie. Jetzt zählt nur die ANREDE:
 *   - am Anfang der Nachricht wie beim Schmied (mit Gruß, mit Satzzeichen danach oder vor einem typischen Redeanfang:
 *     „Vespera, …“, „Hey Vespera …“, „Vespera kannst du …“);
 *   - „Hey/Hallo/Hi/Servus Vespera“ an jeder Stelle (das Wach-Wort, mitten im Satz gesagt);
 *   - in irgendeinem Satz die Anrede nach `redetAn` („Was meinst du, Vespera?“, „Vespera: …“) — nicht nach einem Artikel
 *     („Fragen wir die Vespera?“ redet über sie).
 * „… und Vespera hat dann …“ redet ÜBER sie und holt sie nicht dazu.
 */
const VESPERA_NAME = "vespera";
const AN_VESPERA_MIT_GRUSS_IRGENDWO = new RegExp(`${KEIN_BUCHSTABE_DAVOR}(?:hey|hallo|hi|servus)[\\s,!.]+${VESPERA_NAME}${KEIN_BUCHSTABE}`, "iu");
const AN_VESPERA_AM_ANFANG = new RegExp(
  `${VORNE_SCHMUCK}(?:${GRUSS}[\\s,!.]+${VESPERA_NAME}${KEIN_BUCHSTABE}|${VESPERA_NAME}${KEIN_BUCHSTABE}(?:${NACH_DEM_NAMEN}|\\s+${ANREDE_ANFANG}${KEIN_BUCHSTABE}))`,
  "iu",
);

/*
 * RÜCKGABE MITTEN IM SATZ (Journal 16.10. 09:57: „… damit gebe ich dich jetzt frei und bitte Vespera, antworte mir, ob du das
 * alles verstanden hast“ — der Faden hielt beim Schmied, niemand antwortete, das Mikro ging ohne Antwort auf). Als Anrede an
 * Vespera zählt auch, mitten im Satz:
 *   - „(und) bitte Vespera“ / „(und) jetzt Vespera“, wenn danach ein Satzzeichen oder das Ende folgt;
 *   - „Vespera, antworte …“ / „Vespera, bestätige …“ (Befehl direkt nach dem Komma);
 *   - „ich gebe dich frei … Vespera“, wenn Vespera danach mit Satzzeichen oder am Ende steht.
 * „Vespera hat gesagt …“, „frag Vespera nicht …“, „bitte Vespera nicht fragen“ bleiben Rede ÜBER sie.
 */
const NACH_VESPERA = `${VESPERA_NAME}${KEIN_BUCHSTABE}`;
const RUECKGABE_AN_VESPERA = [
  `${KEIN_BUCHSTABE_DAVOR}(?:bitte|jetzt)\\s+${NACH_VESPERA}(?:${NACH_DEM_NAMEN}|\\s*[.!?…]|\\s*$)`,
  `${KEIN_BUCHSTABE_DAVOR}${NACH_VESPERA}\\s*[,:]\\s*(?:antworte|antwortest|bestätige|bestaetige)${KEIN_BUCHSTABE}`,
  `${KEIN_BUCHSTABE_DAVOR}gebe\\s+(?:dich|ihn)\\s+(?:jetzt\\s+)?frei[^.!?\\n]{0,80}?${KEIN_BUCHSTABE_DAVOR}${NACH_VESPERA}\\s*(?:[,:.!?…]|$)`,
].map((m) => new RegExp(m, "iu"));

/** Kommt der Schmied (in einer seiner Schreibweisen) irgendwo in der Nachricht vor — als Anrede oder nicht? Rein. (16.10.2026,
 *  server/adressat-jev.ts: nur dann lohnt die Frage an Jev ohne Faden und ohne Beitrag im Verlauf.) */
export function nenntSchmied(nachricht: string): boolean {
  return new RegExp(`${KEIN_BUCHSTABE_DAVOR}${SCHMIED_RUFNAME}${KEIN_BUCHSTABE}`, "iu").test(nachricht ?? "");
}

/*
 * DER SCHMIED MITTEN IM SATZ (16.10.2026, Journal 21:36–21:38: „Hey Westbauer … Schmid …“ ging als privat an Vespera)
 *
 * Für den Entwickler-Bezug (server/adressat-jev.ts — dort NUR bei offenem Faden gewertet) zählen die Schreibweisen Schmied, Schmid
 * und Schmiet an jeder Stelle. „Schmidt“ NICHT: mitten im Satz ist das fast immer ein echter Nachname (Prüfung 16.10. abends:
 * „Mein Nachbar Schmidt hat mich beschimpft“); als Anrede am Anfang gilt es weiter (`anSchmiedGerichtet`). Dazu die Vorsicht für
 * Menschen dieses Namens:
 *   - NICHT direkt nach Herr/Frau/Dr./Familie, Nachbar(in), Kollege/Kollegin, Onkel/Tante, Hausarzt/Hausärztin und Verwandten oder
 *     Freunden (Schwager/Schwägerin, Bruder/Schwester, Cousin/Cousine, Opa/Oma, Vater/Mutter, Freund/Freundin);
 *   - NICHT vor einem Nachnamen-Hinweis: Doppelname („Schmid-Müller“), Firma („Schmid GmbH“, „& Partner“), „von nebenan“, eine
 *     Beifügung mit „mein/meine/unser …“ („beim Schmid, meinem Hausarzt“).
 */
const SCHMIED_IM_SATZ = "(?:schmied|schmiet|schmid)";
const NAME_DAVOR =
  /(?<!\p{L})(?:herr|herrn|frau|hr|fr|dr|doktor|prof|professor|familie|fam|nachbar|nachbarn|nachbarin|kollege|kollegen|kollegin|onkel|tante|hausarzt|hausärztin|schwager|schwägerin|bruder|schwester|cousin|cousine|opa|oma|vater|mutter|freund|freundin)\.?\s+$/iu;
const NACHNAME_DANACH =
  /^(?:-\p{L}|\s*&|\s+(?:gmbh|kg|ag|og|e\.\s?u\.|und\s+partner|und\s+söhne|von\s+nebenan)(?!\p{L})|\s*,\s*(?:mein|meine|meinem|meinen|meiner|unser|unsere|unserem|unseren|unserer)(?!\p{L}))/iu;

/** Nennt die Nachricht den Schmied (Schmied/Schmid/Schmiet, jede Stelle) — keinen Menschen dieses Namens (Regeln oben)? Rein. */
export function nenntSchmiedImSatz(nachricht: string): boolean {
  const t = nachricht ?? "";
  for (const m of t.matchAll(new RegExp(`${KEIN_BUCHSTABE_DAVOR}${SCHMIED_IM_SATZ}${KEIN_BUCHSTABE}`, "giu"))) {
    const i = m.index ?? 0;
    if (NAME_DAVOR.test(t.slice(Math.max(0, i - 20), i)) || NACHNAME_DANACH.test(t.slice(i + m[0].length))) continue;
    return true;
  }
  return false;
}

/** Spricht Martin in dieser Nachricht Vespera an (Regeln oben) — und nicht nur über sie? Rein. */
export function anVesperaGerichtet(nachricht: string): boolean {
  const t = (nachricht ?? "").trim();
  if (!t) return false;
  if (AN_VESPERA_AM_ANFANG.test(t) || AN_VESPERA_MIT_GRUSS_IRGENDWO.test(t) || anVorsatzAmAnfang(t, VESPERA_NAME)) return true;
  if (RUECKGABE_AN_VESPERA.some((re) => re.test(t))) return true;
  return t
    .split(/(?<=[.!?…])\s+|\n+/)
    .some((s) => redetAnMuster(s.trim(), VESPERA_NAME));
}

/*
 * DER ABSCHIED AN DEN SCHMIED (Martin, 16.10.2026)
 *
 * Sagt Martin „Danke, Schmied, das war's“ / „Tschüss Schmied“ — oder verabschiedet Vespera ihn („Danke, Schmied, bis später!“) —,
 * soll das Mikro NICHT auf einen Beitrag von ihm warten: es käme keiner, und das Mikro hinge bis zur Frist (SCHMIED_WARTEN_MS).
 *
 * Als Abschied zählt nur ein KLARER: der Text (bei Vespera der Schluss ab ihrer letzten Anrede an ihn) besteht NUR aus Name,
 * Gruß-/Füllwort, Dank und mindestens einem Abschiedswort (shared/abschied-worte.ts, dieselben Wörter wie die Reflex-Antwort,
 * dazu „das war's“ und „gute Nacht“) — höchstens ABSCHIED_HOECHSTENS_WOERTER Wörter. Steht noch etwas dabei („Tschüss, aber
 * prüf noch das Journal“), ist es kein Abschied, und es bleibt beim Warten. Nur ein Dank („Danke, Schmied.“) auch nicht: auf
 * einen Dank antwortet er vielleicht noch.
 * Der Notschalter `SCHMIED_ABSCHIED=0` sitzt am Server (server/dreier-modus.ts, `schmiedAbschiedAn`).
 */
const ABSCHIED_HOECHSTENS_WOERTER = 12;
/** Das Ende eines Gesprächs über den Abschied der Reflex-Antwort hinaus (Martin: „Danke, das war's“). */
const GESPRAECH_ENDE = "(?:das war s|das wars|das war es)(?: (?:schon|erstmal|erst mal|für heute|fuer heute|fürs erste))?|gute nacht";
/** Weitere Abschiedsgrüße, die nur hier zählen (die Reflex-Antwort bleibt eng). */
const ABSCHIED_GRUSS = "ciao|tschau|servus|auf wiederhören|auf wiederhoeren|wiederhören|wiederhoeren|machs gut|schönen (?:tag|abend|feierabend)|schönes wochenende";
const ABSCHIED_WORT = `(?:${ABSCHIED}|${GESPRAECH_ENDE}|${ABSCHIED_GRUSS})`;
/** Zustimmung und Füllwörter, die einem Abschied vorangehen dürfen („Ok, passt, dann bis später“). */
const ZUSTIMMUNG = "(?:passt|wunderbar|ja|jo|jep|genau|na|nun|dann|also|gerne|gern|sehr gut|in ordnung|geht klar|bestens|alles gut)";
/** Was neben dem Abschied stehen darf: der Name, ein Gruß- oder Füllwort, ein Dank. */
const ABSCHIED_BEIWORT = `(?:${SCHMIED_RUFNAME}|lieber|liebe|hey|hallo|hi|also|so|und|dann|auch|dir|euch|${FUELLER}|${ZUSTIMMUNG}|${DANK})`;
/** Das Muster „nur Abschied“ für den Namen eines Entwicklers (Zwischenspeicher: der Schmied wird am häufigsten gefragt). */
const NUR_ABSCHIED_JE_ID = new Map<EntwicklerId, RegExp>();
function nurAbschied(id: EntwicklerId): RegExp {
  let re = NUR_ABSCHIED_JE_ID.get(id);
  if (!re) {
    const beiwort = ABSCHIED_BEIWORT.replace(SCHMIED_RUFNAME, entwickler(id).rufname);
    re = new RegExp(`^(?:${beiwort} )*${ABSCHIED_WORT}(?: (?:${beiwort}|${ABSCHIED_WORT}))*$`, "u");
    NUR_ABSCHIED_JE_ID.set(id, re);
  }
  return re;
}

function istKlarerAbschied(text: string, id: EntwicklerId = "schmied"): boolean {
  const t = bereinigt(text);
  if (!t || t.split(" ").length > ABSCHIED_HOECHSTENS_WOERTER) return false;
  return nurAbschied(id).test(t);
}

/** Ist Martins Nachricht an den Schmied ein klarer Abschied (Regeln oben)? Rein. */
export function abschiedAnSchmied(nachricht: string): boolean {
  return istKlarerAbschied(nachricht ?? "");
}

/** Ist Martins Nachricht an diesen Entwickler (Zimmermann, Hüter) ein klarer Abschied (dieselben Regeln wie beim Schmied)? Rein. */
export function abschiedAnEntwickler(nachricht: string, id: EntwicklerId): boolean {
  return istKlarerAbschied(nachricht ?? "", id);
}

/**
 * Der EINE Satz von Vesperas Antwort mit ihrer LETZTEN Anrede an den Schmied (aus den letzten drei Sätzen des letzten Absatzes) —
 * `null`, wenn sie ihn dort nicht anspricht (`sprichtSchmiedAn`). Nur dieser Satz geht ihm bei einer Übergabe zu: nichts davor, nichts
 * danach (im Zweifel zu wenig, nie zu viel). Rein.
 */
export function schlussAbAnredeSchmied(text: string): string | null {
  return schlussAbAnredeEntwickler(text, "schmied");
}

/** Wie `schlussAbAnredeSchmied`, für jeden Entwickler (Übergabe an Zimmermann/Hüter). Rein. */
export function schlussAbAnredeEntwickler(text: string, id: EntwicklerId): string | null {
  if (!sprichtEntwicklerAn(text, id)) return null;
  const name = entwickler(id).rufname;
  const saetze = schlussSaetze(text);
  for (let i = saetze.length - 1; i >= 0; i--) if (redetAnMuster(saetze[i] ?? "", name)) return saetze[i] ?? null;
  return null;
}

/**
 * Verabschiedet Vespera am Ende ihrer Antwort den Schmied? Nur wenn sie ihn dort anspricht (`sprichtSchmiedAn`) und der Schluss
 * ab ihrer LETZTEN Anrede an ihn ein klarer Abschied ist (Regeln oben). Rein.
 */
export function verabschiedetSchmied(text: string): boolean {
  if (!sprichtSchmiedAn(text)) return false;
  const saetze = schlussSaetze(text);
  let ab = -1;
  for (let i = saetze.length - 1; i >= 0 && ab < 0; i--) if (redetAnMuster(saetze[i] ?? "", SCHMIED_RUFNAME)) ab = i;
  return ab >= 0 && istKlarerAbschied(saetze.slice(ab).join(" "));
}
